import { cn, focusRing } from "@simpro/ui";
import { ArrowRight, Clock, Plus } from "lucide-react";
import { useId, useState } from "react";
import { Link } from "react-router";
import { useSalonMoney } from "@/features/salon";
import { SectionHeading } from "@/shared/components/SectionHeading";
import { Reveal } from "@/shared/motion/Reveal";
import { page } from "@/shared/styles/layout";
import { useUnstaffedServices } from "../hooks/useUnstaffedServices";
import { cheapest } from "../lib/cheapest";
import { formatDuration } from "../lib/formatDuration";
import { MAX_SERVICES, type PublicCatalog, type PublicCatalogCategory } from "../schemas/booking.schemas";
import { PlusCheck } from "./AddServiceButton";
import { CallToBook } from "./CallToBook";

export interface RitualsMenuProps {
  catalog: PublicCatalog;
  /** In the order they were added, which is the order they are done in. */
  selected: readonly string[];
  onToggle: (serviceId: string) => void;
  /** The salon's own heading (FR-SA-191); "Our services" when it wrote none. */
  heading?: string | null;
  /** A picture per menu heading, chosen by the salon (FR-SA-191), before the first service's own. */
  images?: Readonly<Record<string, { url: string; altText: string }>>;
  /** A line per menu heading, written by the salon (FR-SA-191), over its treatments. */
  texts?: Readonly<Record<string, string>>;
}

/** The board's panel colours, in turn: pale sage, blush and sand. */
const PANELS = ["bg-muted-bg", "bg-tint", "bg-surface"] as const;

/**
 * The menu as the Organic Wellness board's "Rituals" (design note salon-booking-organic-wellness.md
 * section 6 item 5; TDD-SA FR-SA-190, FR-SA-138, FR-SA-202). One rounded panel per menu heading, tinted
 * pale sage, blush or sand in turn, with the heading's picture, its name in the display face, how many
 * treatments it has and its lowest price, and a round + that turns into a × as the panel opens. Open,
 * the panel shows each treatment on a cream card with its price, its time and an Add pill that the
 * basket bar carries into booking. One panel is open at a time, the first to begin with.
 *
 * It takes the place of the earlier headings cards and price list. A closed panel's treatments are
 * inert, so the keyboard and a screen reader meet only what is shown.
 */
export function RitualsMenu({ catalog, selected, onToggle, heading, images = {}, texts = {} }: RitualsMenuProps) {
  const categories = catalog.filter((category) => category.services.length > 0);
  // Untouched (`undefined`), the first heading is open, whenever the menu arrives; once the visitor
  // opens or closes one, their choice stands.
  const [chosen, setChosen] = useState<string | null | undefined>(undefined);
  const byPhone = useUnstaffedServices(categories.flatMap((category) => category.services.map((service) => service.id)));
  if (categories.length === 0) return null;
  const open = chosen === undefined ? (categories[0]?.id ?? null) : chosen;
  const full = selected.length >= MAX_SERVICES;

  return (
    <section aria-labelledby="services-heading" className={cn(page, "flex flex-col gap-12")}>
      <SectionHeading
        id="services-heading"
        title={heading ?? "Our services"}
        lead="Open a heading to see each treatment. Add as many as you like, and we will plan the time around you."
      />
      <ul className="flex flex-col gap-4">
        {categories.map((category, index) => (
          <Reveal as="li" key={category.id} index={index}>
            <Panel
              category={category}
              tone={PANELS[index % PANELS.length] ?? "bg-surface"}
              image={images[category.id] ?? null}
              line={texts[category.id] ?? null}
              open={open === category.id}
              onToggle={() => setChosen(open === category.id ? null : category.id)}
              selected={selected}
              full={full}
              byPhone={byPhone}
              onAdd={onToggle}
            />
          </Reveal>
        ))}
      </ul>
    </section>
  );
}

function Panel({
  category,
  tone,
  image,
  line,
  open,
  onToggle,
  selected,
  full,
  byPhone,
  onAdd,
}: {
  category: PublicCatalogCategory;
  tone: string;
  image: { url: string; altText: string } | null;
  line: string | null;
  open: boolean;
  onToggle: () => void;
  selected: readonly string[];
  full: boolean;
  /** Services no stylist does online: "Call to book" in place of Add. */
  byPhone: ReadonlySet<string>;
  onAdd: (serviceId: string) => void;
}) {
  const money = useSalonMoney();
  const panelId = useId();
  const picture = image?.url ?? category.services.find((service) => service.imageUrl)?.imageUrl ?? null;
  const from = cheapest(category.services);
  const count = category.services.length;

  return (
    <div className={cn("overflow-hidden rounded-[2.25rem] transition-colors duration-500", tone)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className={cn(
          "group grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 p-3 text-left text-heading md:gap-6 md:p-4",
          focusRing,
        )}
      >
        <span className="grid aspect-square w-[clamp(4.5rem,10vw,7.5rem)] place-items-center overflow-hidden rounded-[1.75rem] bg-sage-tint">
          {picture ? (
            <img
              src={picture}
              alt=""
              loading="lazy"
              className="size-full object-cover transition-transform duration-[800ms] group-hover:scale-[1.08] motion-reduce:transition-none"
            />
          ) : (
            <span aria-hidden="true" className="headline text-[2.5rem] text-heading">
              {category.name.trim().charAt(0)}
            </span>
          )}
        </span>
        <span className="flex min-w-0 flex-col gap-1">
          <span className="headline block truncate text-[clamp(2rem,4vw,3.25rem)] leading-none">{category.name}</span>
          <span className="text-[15px] text-subtle">
            {count} {count === 1 ? "treatment" : "treatments"}
            {from ? ` · from ${money(from)}` : ""}
          </span>
        </span>
        <span
          aria-hidden="true"
          className={cn(
            "mr-1 grid size-[3.25rem] place-items-center rounded-full ring-[1.5px] ring-heading ring-inset transition-transform duration-500 ease-[cubic-bezier(0.3,1.3,0.5,1)] motion-reduce:transition-none md:mr-3",
            open && "rotate-45",
          )}
        >
          <Plus className="size-[18px]" strokeWidth={2} />
        </span>
      </button>

      <div
        id={panelId}
        inert={!open}
        className={cn(
          "grid transition-[grid-template-rows] duration-[600ms] ease-[cubic-bezier(0.3,1,0.4,1)] motion-reduce:transition-none",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-4 px-4 pb-7 md:px-8">
            {line ? <p className="max-w-2xl text-[15px] leading-relaxed text-content">{line}</p> : null}
            <ul className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,17.5rem),1fr))] gap-3">
              {category.services.map((service) => {
                const added = selected.includes(service.id);
                return (
                  <li
                    key={service.id}
                    className="flex flex-col gap-2.5 rounded-[1.5rem_1.5rem_1.5rem_0.375rem] bg-base p-[1.375rem]"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="headline text-[1.625rem] leading-tight text-heading">{service.name}</h3>
                      <span className="shrink-0 font-semibold text-heading tabular-nums">{money(service.price)}</span>
                    </div>
                    {service.description ? (
                      <p className="line-clamp-3 text-[15px] leading-relaxed text-content">{service.description}</p>
                    ) : null}
                    <div className="mt-auto flex items-center justify-between gap-3 pt-1.5">
                      <span className="flex items-center gap-1.5 text-sm text-subtle">
                        <Clock className="size-3.5" aria-hidden />
                        {formatDuration(service.durationMinutes)}
                      </span>
                      {byPhone.has(service.id) && !added ? (
                        <CallToBook serviceName={service.name} />
                      ) : (
                      <button
                        type="button"
                        aria-pressed={added}
                        aria-label={`Add ${service.name}`}
                        disabled={!added && full}
                        onClick={() => onAdd(service.id)}
                        className={cn(
                          "inline-flex h-10 items-center gap-1.5 rounded-full px-[1.125rem] text-sm font-semibold transition-[background-color,color,box-shadow] duration-400 ease-[cubic-bezier(0.3,1.2,0.5,1)] disabled:opacity-40",
                          added
                            ? "bg-ink text-on-ink ring-[1.5px] ring-ink ring-inset"
                            : "text-heading ring-[1.5px] ring-heading ring-inset hover:bg-muted-bg",
                          focusRing,
                        )}
                      >
                        <PlusCheck on={added} className="size-4" />
                        {added ? "Added" : "Add"}
                      </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <Link
              to={`/services#menu-${encodeURIComponent(category.id)}`}
              viewTransition
              className={cn(
                "group/link inline-flex items-center gap-1.5 self-start rounded-sm text-sm font-semibold text-heading",
                focusRing,
              )}
            >
              <span className="link-draw">See {category.name} on the menu</span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover/link:translate-x-0.5" aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
